import React, { useContext, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { FaHeart, FaRegHeart, FaCrown, FaStar } from "react-icons/fa";
import { FiMapPin, FiShare2 } from "react-icons/fi";
import { MdVerified } from "react-icons/md";
import { FaShieldAlt } from "react-icons/fa";
import "./Item.css";
import useFavorite from "../hooks/useFavorite";
import { useAuth } from "../context/AuthContext";
import { ShopContext } from "../context/ShopContext";
import { findListing } from "../lib/findListing";
import { priceText, thumb } from "../api";
import { trackPromoClick } from "../lib/boost";
import { regionLabel } from "../data/regions";
import { replyLabel } from "../lib/listingFilters";
import ContactModal from "./product/ContactModal";

const away = (km) => (km < 1 ? "1 km away" : `${Number.isInteger(km) ? km : km.toFixed(1)} km away`);
const initials = (n = "") => n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

/**
 * Listing card (Faji style): photo with share + save buttons, category label, title, price,
 * location • condition, seller pill, and a Contact button next to a share button.
 * Pass the whole listing as `listing` (preferred); the individual props still work for older callers.
 *   compact → photo, title, price and location only (used for "related" strips)
 *   hideSeller → no seller pill / Contact button (used on a seller's own page)
 */
const Item = ({ listing, id, _id, name, images, new_price, priceType, old_price, address, promoted, trust, specs, compact, hideSeller, distanceKm }) => {
  const shop = useContext(ShopContext);
  const L = listing || findListing(shop?.all_product, _id) || {};
  const pid = _id || L._id;
  const href = id || L.id || pid;
  const title = name ?? L.title;
  const imgs = images || L.images;
  const firstImage = Array.isArray(imgs) && imgs.length > 0 ? imgs[0] : "";
  const original = typeof firstImage === "string" && firstImage !== "" ? firstImage : "/placeholder.jpg";
  const [imgSrc, setImgSrc] = useState(() => thumb(original, 480));   // resized copy for the grid; falls back to the original
  const { favorited: liked, toggle } = useFavorite(pid);
  const { user, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [contacting, setContacting] = useState(false);
  const [copied, setCopied] = useState(false);

  const price = new_price ?? L.price;
  const ptype = priceType ?? L.priceType;
  const oldPrice = old_price ?? L.old_price;
  const isPromoted = promoted ?? L.promoted;
  const t = trust || L.trust || {};
  // seller details come with the feeds; if an older server did not send them, the owner id is still enough to message them
  const seller = L.seller || (L.owner ? { id: String(L.owner), name: "Seller", avatar: "" } : null);
  const km = distanceKm ?? L.distanceKm;

  const area = L.city || address;
  const place = km != null ? away(km) : [area, L.region ? regionLabel(L.region) : ""].filter(Boolean).join(", ") || address || "";
  const mine = !!seller && String(seller.id) === String(user?._id || user?.id || "");
  const showSeller = !compact && !hideSeller && !!seller;
  const sellerTip = [t.verified && "ID verified", t.reviews > 0 && `${t.rating?.toFixed(1)}★ from ${t.reviews} review${t.reviews === 1 ? "" : "s"}`, replyLabel(t.replyMins), t.sales > 0 && `${t.sales} completed sale${t.sales === 1 ? "" : "s"}`].filter(Boolean).join(" · ");

  const open = () => { if (isPromoted) trackPromoClick(pid); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const contact = () => {
    if (!isLoggedIn) return navigate(`/login?next=${encodeURIComponent(`/product/${href}`)}`);
    setContacting(true);
  };
  // native share sheet on phones, copy-link everywhere else
  const share = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const url = `${window.location.origin}/product/${pid || href}`;
    try {
      if (navigator.share) { await navigator.share({ title, text: `${title} on TroveHub`, url }); return; }
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    } catch { /* cancelled */ }
  };

  return (
    <article className={`item${compact ? " item-compact" : ""}`}>
      <div className="item-img-wrap">
        <Link to={`/product/${href}`} onClick={open} aria-label={title}>
          <img src={imgSrc} alt={title || "Product"} className="item-img" loading="lazy" decoding="async" onError={() => { if (imgSrc !== original && imgSrc !== "/placeholder.jpg") setImgSrc(original); else if (imgSrc !== "/placeholder.jpg") setImgSrc("/placeholder.jpg"); }} />
        </Link>

        <div className="item-fab">
          {!compact && <button type="button" className="item-round" onClick={share} aria-label="Share listing"><FiShare2 /></button>}
          <button type="button" className="item-round like-btn" onClick={(e) => { e.preventDefault(); if (pid) toggle(); }} aria-label={liked ? "Remove from favorites" : "Save listing"} aria-pressed={liked}>
            {liked ? <FaHeart className="liked" /> : <FaRegHeart />}
          </button>
        </div>

        {isPromoted && <span className="item-promo"><FaCrown /> Featured</span>}
        {!compact && t.escrow && <span className="item-escrow" title="Pay safely: the seller only gets paid after you confirm the item"><FaShieldAlt /> Escrow protected</span>}
      </div>

      <div className="item-info">
        {!compact && L.category && <p className="item-cat">{L.category}</p>}
        <Link to={`/product/${href}`} onClick={open} className="item-name" title={title}>{title}</Link>
        {!compact && specs && <p className="item-specs">{specs}</p>}
        <p className="item-prices">
          <span className="item-price-new">{priceText({ price, priceType: ptype })}</span>
          {oldPrice && <span className="item-price-old">₦{Number(oldPrice).toLocaleString()}</span>}
        </p>
        {(place || L.condition) && (
          <p className="item-location">
            {place && <><FiMapPin className="item-pin" /><span className="item-place">{place}</span></>}
            {place && !compact && L.condition && <i className="item-dot" />}
            {!compact && L.condition && <span className="item-cond">{L.condition}</span>}
          </p>
        )}

        {showSeller && (
          <div className="item-seller" title={sellerTip || undefined}>
            {seller.avatar ? <img src={seller.avatar} alt="" loading="lazy" /> : <span className="item-seller-i">{initials(seller.name)}</span>}
            <span className="item-seller-n">{seller.name}</span>
            {t.verified && <MdVerified className="item-ver" aria-label="ID verified" />}
            {t.reviews > 0 && <span className="item-seller-r"><FaStar /> {t.rating?.toFixed(1)}</span>}
          </div>
        )}

        {showSeller && (
          <div className="item-actions">
            {mine
              ? <Link to={`/product/${href}`} className="item-btn item-btn-ghost" onClick={open}>Your listing</Link>
              : <button type="button" className="item-btn" onClick={contact}>Contact</button>}
            <button type="button" className="item-share" onClick={share} aria-label="Share listing"><FiShare2 />{copied && <span className="item-toast">Link copied</span>}</button>
          </div>
        )}
      </div>

      {contacting && createPortal(
        <ContactModal mode="message" p={{ _id: pid, title, price, priceType: ptype }} seller={seller} onClose={() => setContacting(false)} onOfferSent={() => {}} />,
        document.body
      )}
    </article>
  );
};

export default Item;
